<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import WarningIcon from '@/assets/icons/ui/warning.svg';
import { Route } from '@/modules/core/enums/route.enum.ts';
import type { CounterpartyNameMatch } from '@/modules/counterparty/api/counterparty.api.ts';
import UiSpinner from '@/modules/ui/components/ui/UiSpinner.vue';
import UiTooltip from '@/modules/ui/components/ui/UiTooltip.vue';

const { loading = false, matches = [] } = defineProps<{
  loading?: boolean;
  matches?: CounterpartyNameMatch[];
}>();

const router = useRouter();

const matchLinks = computed(() =>
  matches.map((item) => ({
    id: item.id,
    name: item.name,
    href: router.resolve({ name: Route.CounterpartyPage, params: { id: item.id } }).href,
  }))
);
</script>

<template>
  <div
    v-if="loading || matches.length"
    class="name-match-indicator"
  >
    <UiSpinner
      v-if="loading"
      size="s"
    />

    <UiTooltip
      v-else
      class="warning"
      placement="bottom"
      align="left"
      interactive
      :max-width="320"
    >
      <WarningIcon class="warning-icon" />

      <template #tooltip>
        <div class="tooltip">
          <p class="title">{{ $t('counterparty.master.steps.name.field.similar') }}</p>
          <ul class="list">
            <li
              v-for="item in matchLinks"
              :key="item.id"
              class="item"
            >
              <a
                class="link _blue _hover"
                :href="item.href"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ item.name }}
              </a>
            </li>
          </ul>
        </div>
      </template>
    </UiTooltip>
  </div>
</template>

<style scoped lang="scss">
.name-match-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.warning {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  cursor: default;
}

.warning-icon {
  width: 16px;
  height: 16px;
  fill: var(--color-yellow-800);
}

.tooltip {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.title {
  margin: 0;
  font-size: var(--font-size-m);
  line-height: var(--line-height-m);
  font-weight: var(--font-weight-bold);
}

.list {
  display: flex;
  margin: 0;
  padding: 0;
  list-style: none;
  flex-direction: column;
  gap: 4px;
}

.item {
  margin: 0;
  font-size: var(--font-size-m);
  line-height: var(--line-height-m);
  word-break: break-word;
}

.link {
  text-decoration: underline;
}
</style>
